/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 17-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : Assemble the Trade Templates library model (trades + provider/global current versions
 *                + adopted tenants) from the provider-api reads available today.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getTrades } from '../../services/TradeConfigurationServics';
import { getTenantTradeMap } from '../mapping/services/TenantMappingService';
import {
  resolveCurrentTemplatesBulk, getProviderDraftsBulk, listVersions, getDraft,
  mapApiVersions, flattenEntriesToTasks, TemplateEntry,
} from './services/TradeTemplateVersionService';
import { Trade, Tenant, GlobalDefaultTemplate, TemplateVersion, DraftInfo } from './types';

export interface TradeTemplateData {
  trades: Trade[];
  tenants: Tenant[];
  globalDefault: GlobalDefaultTemplate;
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Lazy-load a tenant's current versions (per adopted trade) into the model — By-tenant view. */
  loadTenantVersions: (tenantId: string) => void;
}

/** Build a single current-version wrapper from resolved per-phase entries. */
function currentVersion(entries: TemplateEntry[]): TemplateVersion {
  const maxVersion = entries.reduce((m, e) => Math.max(m, e.templateVersion ?? 0), 0);
  return {
    version: `v${maxVersion || 1}`,
    note: 'Current version',
    when: '',
    tasks: flattenEntriesToTasks(entries),
    forkedFrom: null,
    status: 'active',
    invalid: false,
    versionGroupUid: entries.find(e => e.versionGroupUid)?.versionGroupUid ?? null,
  };
}

const EMPTY_GLOBAL: GlobalDefaultTemplate = { name: 'Global Default Template', versions: [] };

/** One tenant scope's freshly-loaded published history + its unpublished draft (if any). */
interface LoadedScope { versions: TemplateVersion[]; draft: DraftInfo | null; }

/** Merge freshly-loaded tenant versions + draft into the trades model (immutably), marking each fetched
 *  scope `loaded` so cards can distinguish "no published version" from "still loading". */
function mergeTenantVersions(list: Trade[], tenantId: string, byTrade: Map<string, LoadedScope>): Trade[] {
  return list.map(t => {
    const loaded = byTrade.get(t.id);
    return loaded && t.tenants[tenantId]
      ? { ...t, tenants: { ...t.tenants, [tenantId]: { versions: loaded.versions, loaded: true, draft: loaded.draft } } }
      : t;
  });
}

/**
 * Loads the library model. NOTE (v1 scope): per-scope version HISTORY and arbitrary tenant-scope task
 * reads are not yet exposed by provider-api, so each scope carries only its current version and tenant
 * scopes are listed as adopted (empty history) until those read endpoints are added. The mutation flows
 * (save / draft / promote / make-current) call the real endpoints and then reload.
 */
export function useTradeTemplateData(accessToken: string): TradeTemplateData {
  const [trades, setTrades] = useState<Trade[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [globalDefault, setGlobalDefault] = useState<GlobalDefaultTemplate>(EMPTY_GLOBAL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce(n => n + 1), []);

  // Guards the lazy loadTenantVersions IIFE from calling setTrades after unmount (the main effect has
  // its own `cancelled` flag; this on-demand callback needs the equivalent). Must reset to true at the
  // START of the effect, not just via useRef's initializer: React 18 StrictMode (dev only) mounts every
  // component, fires effects, fires their cleanups, then fires effects again — so the cleanup below runs
  // once immediately on initial mount, and without the reset here `mountedRef.current` is left `false`
  // forever after that dev-mode double-invoke, silently dropping every future setTrades call.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadTenantVersions = useCallback((tenantId: string) => {
    if (!accessToken || !tenantId) return;
    // Fetch each adopted trade this tenant hasn't loaded yet (idempotent via the `loaded` flag).
    const targets = trades.filter(t => t.tenants[tenantId] && !t.tenants[tenantId].loaded);
    if (!targets.length) return;
    void (async () => {
      // allSettled, not all: a single trade's fetch throwing (e.g. a transport-level error out of
      // ApiBase) must not drop the whole tenant's batch — with Promise.all, one rejection silently
      // kills the update for every trade, leaving all of them stuck on "loading…" forever with no
      // error shown anywhere.
      const results = await Promise.allSettled(targets.map(async t => {
        const [vr, dr] = await Promise.all([
          listVersions(Number(t.id), 'tenant', tenantId, accessToken),
          getDraft(Number(t.id), 'tenant', tenantId, accessToken),
        ]);
        const draft: DraftInfo | null = dr.ok && dr.data?.hasDraft
          ? { taskCount: flattenEntriesToTasks(dr.data.entries).length, when: dr.data.updatedAt ?? null }
          : null;
        return { tradeId: t.id, versions: vr.ok ? mapApiVersions(vr.data?.versions) : [], draft };
      }));
      if (!mountedRef.current) return; // unmounted mid-fetch — don't update state
      // Merge EVERY settled result (even empty, even rejected) so every fetched scope is marked
      // loaded — no perpetual spinner. A rejected trade is logged and merged in loaded-but-empty
      // rather than left stuck; the user can re-expand the tenant to retry it.
      const byTrade = new Map<string, LoadedScope>();
      results.forEach((r, i) => {
        const tradeId = targets[i].id;
        if (r.status === 'fulfilled') {
          byTrade.set(r.value.tradeId, { versions: r.value.versions, draft: r.value.draft });
        } else {
          console.warn(`[useTradeTemplateData] Failed to load tenant ${tenantId}'s versions for trade ${tradeId}`, r.reason);
          byTrade.set(tradeId, { versions: [], draft: null });
        }
      });
      setTrades(prev => mergeTenantVersions(prev, tenantId, byTrade));
    })();
  }, [accessToken, trades]);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const [res, globalRes, mappings] = await Promise.all([
          getTrades(accessToken),
          listVersions(0, 'global', null, accessToken), // tradeId ignored for the global scope
          getTenantTradeMap(accessToken),               // all adoptions in one Provider-DB query
        ]);
        if (!res?.ok || !Array.isArray(res.data)) {
          if (!cancelled) { setError(res?.message ?? 'Failed to load trades'); setTrades([]); }
          return;
        }

        // Build tenant lists per trade (and the global tenant list) from the single mapping read.
        const tenantMap = new Map<string, string>();
        const tenantsByTrade = new Map<number, string[]>();
        mappings.forEach(m => {
          tenantMap.set(m.tenantId, m.tenantName);
          const arr = tenantsByTrade.get(m.tradeId) ?? [];
          if (!arr.includes(m.tenantId)) arr.push(m.tenantId);
          tenantsByTrade.set(m.tradeId, arr);
        });

        // Bulk-resolve EVERY trade's current template in ONE request (was: one resolveCurrentTemplate
        // call per trade, all fired in parallel — with a large trade catalog that alone opened as many
        // concurrent DB connections as there are trades; adding a second per-trade call for provider
        // drafts doubled it, enough to exhaust Postgres's max_connections). See
        // TradeTemplateVersionService.resolveCurrentTemplatesBulk/getProviderDraftsBulk (single pooled
        // connection, batched `trade_id = ANY(?)` queries, server-side).
        const tradeIds = res.data.map((t: Record<string, unknown>) => Number(t.id));
        const bulkResolved = await resolveCurrentTemplatesBulk(tradeIds, accessToken);
        const resolvedByTrade = bulkResolved?.data ?? {};

        // Fetch drafts for EVERY trade, not just ones with a published provider entry — the "+" Add
        // Provider Default flow starts a trade with NO published version at all (seeded from the
        // Global Default), and its draft must still show as a Draft card. getProviderDraftsBulk is one
        // batched query regardless of how many tradeIds are passed, so this costs nothing extra.
        const bulkDrafts = tradeIds.length
          ? await getProviderDraftsBulk(tradeIds, accessToken).catch(e => {
              console.warn('[useTradeTemplateData] Failed to bulk-load provider drafts', e);
              return null;
            })
          : null;
        const draftsByTrade = bulkDrafts?.data ?? {};

        const built = res.data.map((t: Record<string, unknown>) => {
          const tradeId = Number(t.id);
          const resolved = resolvedByTrade[tradeId];
          const source = resolved?.source;
          const entries = resolved?.entries ?? [];
          const hasProvider = source === 'provider' && entries.length > 0;

          const draftEntry = draftsByTrade[tradeId];
          const providerDraft: DraftInfo | null = draftEntry?.hasDraft
            ? { taskCount: flattenEntriesToTasks(draftEntry.entries).length, when: draftEntry.updatedAt ?? null }
            : null;
          // A draft-only scope (no published version yet, e.g. one just started via "+") must still
          // produce a `provider` object so its Draft card can render — versions stays empty in that case.
          const provider = (hasProvider || providerDraft)
            ? { versions: hasProvider ? [currentVersion(entries)] : [], draft: providerDraft }
            : null;

          const tenantScopes: Trade['tenants'] = {};
          (tenantsByTrade.get(tradeId) ?? []).forEach(tid => {
            tenantScopes[tid] = { versions: [] }; // full history lazy-loaded when the scope is opened
          });

          const trade: Trade = {
            id: String(t.id),
            name: String(t.name ?? `Trade ${t.id}`),
            category: String(t.type ?? 'standard'),
            provider,
            tenants: tenantScopes,
          };
          return trade;
        });

        if (cancelled) return;
        setTrades(built);
        setTenants(Array.from(tenantMap.entries()).map(([id, name]) => ({ id, name })));
        const globalVersions = mapApiVersions(globalRes?.data?.versions);
        setGlobalDefault(globalVersions.length ? { name: 'Global Default Template', versions: globalVersions } : EMPTY_GLOBAL);
      } catch (e) {
        if (!cancelled) setError((e as Error)?.message ?? 'Failed to load trade templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [accessToken, nonce]);

  return { trades, tenants, globalDefault, loading, error, reload, loadTenantVersions };
}
